import React, {
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
} from "react";
import Label from "./Label";

export default function ObjectField({
  id,
  name,
  label,
  value,
  className,
  labelStyle,
  labelAs,
  children,
  onChange,
  disabled,
  isInvalid,
  error,
  ...props
}) {
  const inputRef = useRef();
  const handleChange = useMemo(
    () => (event) => {
      if (event.target === inputRef.current) {
        if (onChange instanceof Function) {
          onChange(event);
        }
      } else {
        event.stopPropagation();
      }
    },
    [onChange],
  );
  const setProperty = (property, newValue) => {
    inputRef.current.value = {
      ...(value || {}),
      [property]: newValue,
    };
    inputRef.current.name = name;
    inputRef.current.dispatchEvent(
      new Event("change", { bubbles: true, cancelable: true, composed: false }),
    );
  };
  const computedClassName = [
    "object-field",
    "form-group",
    (isInvalid && "has-error") || "",
    ...className.split(" "),
  ]
    .join(" ")
    .trim();
  useEffect(() => {
    const input = inputRef.current;
    const onChange = handleChange;
    if (!!input) {
      input.addEventListener("change", onChange);
      return () => {
        input.removeEventListener("change", onChange);
      };
    }
  }, [handleChange]);
  return (
    <div className={computedClassName} {...props} ref={inputRef}>
      {!!label &&
        React.cloneElement(
          labelAs,
          { className: "object-field-label", style: labelStyle },
          label,
        )}
      <div className="object-field-wrapper">
        {children instanceof Function &&
          children({
            value,
            setProperty,
            disabled,
          })}
        {isValidElement(children) &&
          cloneElement(children, {
            value,
            setProperty,
            disabled,
          })}
      </div>
      {isInvalid && !!error && <span className="help-block">{error}</span>}
    </div>
  );
}

ObjectField.defaultProps = {
  className: "",
  disabled: false,
  labelAs: <Label />,
};
